import {
  arrayUnion,
  collection,
  doc,
  DocumentData,
  getDocs,
  query,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Discount,
  OtherInformationAnswer,
  TicketOwner,
  TicketType,
  UserBasicData,
} from "../../../utils/types";
import { CustomerHeader } from "../../../components/header";
import {
  OrderSummary,
  OrderSummaryMobileView,
} from "../../../components/customer/event.order-summary";
import {
  BuyTicketStepOne,
  BuyTicketStepThree,
  BuyTicketStepTwo,
} from "../../../components/customer/event.buy-tickets-steps";
import { Loading } from "../../../components/loading";
import { toast } from "react-toastify";
import { firestoredb } from "../../../utils/firebase";
import moment from "moment";
import {
  formatFirestoreTimestamp,
  formatMoney,
  // getPaystackFee,
  isEmailValid,
} from "../../../utils";
import axios from "axios";
import { PhoneNumberUtil } from "google-libphonenumber";
import { PAGE_STEPS } from "../event";
// import Paystack from "@paystack/inline-js";
import { initNombaPayment, listenForNombaPayment } from "../utils/nomba-payment";
import { NombaPaymentModal } from "../components/NombaPaymentModal";


const phoneUtil = PhoneNumberUtil.getInstance();

const isPhoneValid = (phone: string) => {
  try {
    return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone));
  } catch (error) {
    return false;
  }
};
const MOBILE_BREAKPOINT = 1025;

export function BuyEventTicketScreen({
  event,
  handleMoveToPreviousScreen,
  setPageStep,
}: {
  event: DocumentData;
  handleMoveToPreviousScreen: () => void;
  setPageStep: (step: string) => void;
}) {
  const [discount, setDiscount] = useState<Discount | null>(null);
  const [discountCode, setDiscountCode] = useState("");
  const [step, setStep] = useState(1); // from steps 1-3, 4th screen is for mobile (order-summary)
  const [loading, setLoading] = useState(false);
  const [discountLoading, setDiscountLoading] = useState(false);
  const [ticketOwners, setTicketOwners] = useState<TicketOwner[]>([]);
  const [otherInfoAnswers, setOtherInfoAnswers] = useState<
    OtherInformationAnswer[] | []
  >([]);
  const [primaryTicketOwner, setPrimaryTicketOwner] =
    useState<TicketOwner | null>(null);
  const [isMobile, setIsMobile] = useState(
    window.innerWidth < MOBILE_BREAKPOINT
  );
  const [selectedTickets, setSelectedTickets] = useState<TicketType[]>([]);
  const newUrlSearchParams = new URLSearchParams(window.location.search);
  const referee = newUrlSearchParams.get("referee");

  // Add new state
  const [paymentModal, setPaymentModal] = useState<{
    checkoutLink: string;
    orderReference: string;
  } | null>(null);
  const unsubRef = useRef<(() => void) | null>(null);

  const openNombaPaymentPopup = async (
    primaryTicketOwner: TicketOwner,
    metadata: any,
    orderId: string
  ) => {
    try {
      const { checkoutLink, orderReference } = await initNombaPayment(
        totalValues.total,
        primaryTicketOwner.email,
        metadata
      );

      setPaymentModal({ checkoutLink, orderReference });

      // Start listening for payment confirmation
      unsubRef.current = listenForNombaPayment(
        orderReference,
        () => {
          setPaymentModal(null);
          setPageStep(PAGE_STEPS.PAYMENT_SUCCESS);
        },
        (error) => {
          setPaymentModal(null);
          toast.error(error);
          setLoading(false);
        }
      );
    } catch {
      toast.error("There was an issue making payment, please try again!");
      setLoading(false);
    }
  };

  // Handle modal close (user cancelled)
  const handlePaymentModalClose = () => {
    unsubRef.current?.();
    unsubRef.current = null;
    setPaymentModal(null);
    setLoading(false);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => { unsubRef.current?.(); };
  }, []);

  useEffect(() => {
    if (!event?.id) return;
    let ticketTypes = [];
    if (event?.types) {
      ticketTypes = event?.types
        .filter((el: any) => !el?.hide)
        .map((el: any) => {
          return {
            name: el.title,
            price: el.price,
            quantity: 0,
            id: el.id,
            for: el?.for ?? 1,
            maxBuy: el?.maxBuy,
            description: el?.description,
            isClosed: el?.isClosed ?? false,
            orderLimit: el?.noOfTickets,
          };
        });
    } else {
      ticketTypes = [
        {
          name: "General Admission",
          price: event?.amount ?? 0,
          quantity: 1,
          id: "general-admission",
          for: 1,
          isClosed: false,
        },
      ];
    }

    for (let i = 0; i < ticketTypes.length; i++) {
      const ticketType = ticketTypes[i];
      getDocs(
        query(
          collection(firestoredb, "tickets"),
          where("ticketType", "==", ticketType.id),
          where("eventId", "==", event.id)
        )
      ).then((snapshot) => {
        ticketTypes[i] = {
          ...ticketType,
          ticketsBought: snapshot.size,
        };
      });
    }
    setSelectedTickets(ticketTypes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.id]);

  useEffect(() => {
    const handleResize = () => {
      const currentlyMobile = window.innerWidth < MOBILE_BREAKPOINT;
      setIsMobile(currentlyMobile);
    };

    window.addEventListener("resize", handleResize);
    // Initial check
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [MOBILE_BREAKPOINT]);

  const otherQuestions = useMemo(() => {
    const questions = [
      ...(event?.nonRequiredInfo ?? []).map((el: any) => ({
        question: el,
        required: false,
      })),
      ...(event?.otherRequiredInfo ?? []).map((el: any) => ({
        question: el,
        required: true,
      })),
    ];
    // this only renders once
    setOtherInfoAnswers(
      questions.map((el, index) => ({
        question: el.question,
        answer: "",
        index,
        required: el.required,
      }))
    );
    return questions;
  }, [event]);

  useEffect(() => {
    if (isMobile) return;
    if (!isMobile && step === 4) {
      if (otherQuestions.length) return setStep(3);
      else return setStep(2);
    }
  }, [otherQuestions.length, isMobile, step]);

  function getFeeOnTotal(total: number) { 
    return (total * 0.03) + 100; 
  }

  function feeOnOrganiser() {
    if (!event?.applyDingFee || !event?.bearDingFee) return 0;
    const actualAmount = totalValues.total; // the amount of all tickets minus discount (if discount is applied, of course)
    return getFeeOnTotal(actualAmount);

    // let percent = 0.015;
    // if (event?.point5Fee) {
    //   percent = 0.005;
    // }
    // return (
    //   getPaystackFee(actualAmount) +
    //   actualAmount * percent +
    //   (actualAmount < 2500 ? 100 : 0)
    // );
  }

  const totalValues = useMemo(() => {
    let total = selectedTickets.reduce((acc, el) => {
      return acc + el.price * (el?.quantity ?? 0);
    }, 0);
    let ticketToBeDiscounted = null;
    if (discount?.ticketTypeId) {
      // this is for ticket specific discount
      ticketToBeDiscounted = selectedTickets.find(
        (ticket) =>
          ticket.id === discount.ticketTypeId &&
          ticket.price > 0 &&
          ticket.quantity
      );
      // the discount applies to the total price of ticket
      if (ticketToBeDiscounted && ticketToBeDiscounted.quantity)
        ticketToBeDiscounted = {
          ...ticketToBeDiscounted,
          price: ticketToBeDiscounted?.price * ticketToBeDiscounted?.quantity,
        };
    } else {
      // this discounts on the total sum of tickets being bought
      ticketToBeDiscounted = { price: total };
    }
    let discountValue = 0;
    if (discount != null && ticketToBeDiscounted) {
      const price = ticketToBeDiscounted.price ?? 0; // this is either a total of all tickets or for a particular ticketType [depending on discount]
      if (discount.type === "percentage") {
        discountValue = (discount.value / 100) * price;
      } else {
        discountValue = discount.value;
      }
    }
    const totalQuantity = selectedTickets.reduce(
      (acc, curr) => acc + (curr?.quantity ?? 0),
      0
    );
    total -= discountValue;

    let initialTotal = total;

    const newTotal = () => {
      if (!total) return 0;
      if (event?.bearDingFee) return total;

      // 3 percent of the total + 100. period.
      if (event?.applyDingFee) {
        return total + getFeeOnTotal(total);
      }

      return total;

      // if (event?.applyDingFee) {
      //   let base = 0.03045;
      //   if (event?.point5Fee) {
      //     base = 0.0203;
      //   }
      //   if (total < 2500) {
      //     const x = total * base + 100;
      //     return total + x;
      //   }
      //   let pFee = getPaystackFee(total);
      //   if (pFee < 2000) {
      //     const x = total * base;
      //     return total + x + 101.52;
      //   }
      //   const x = total * 0.005;
      //   total = total + x + 2025;
      //   return total;
      // }
      // const fivePercent = total * 0.05;
      // return total + fivePercent + 100;
      // // return getPaystackFee(total) + total;
    };
    newTotal();

    total = Number(newTotal().toFixed(0));
    if (total < 0) total = 0;

    // end charges math
    return {
      total,
      discount: discountValue ?? 0,
      totalQuantity,
      charges: total - initialTotal,
      subtotal: initialTotal + discountValue,
    };
  }, [
    selectedTickets,
    discount,
    event?.bearDingFee,
    event?.applyDingFee,
    // event?.point5Fee,
  ]);

  const handleEditTicket = (
    el: TicketType,
    action: string,
    quantity?: number
  ) => {
    if (el?.isClosed) return;
    const selectedTicketIndex = selectedTickets.findIndex(
      (t) => t.name === el.name
    );
    if (action === "add" && selectedTicketIndex > -1) {
      const quantity = selectedTickets[selectedTicketIndex].quantity ?? 0;
      if (el.maxBuy && quantity + 1 > el.maxBuy)
        return toast.error(
          `You cannot purchase more than (${el.maxBuy}) of this ticket right now`
        );
      if (
        el?.orderLimit &&
        (el?.ticketsBought ?? 0) + quantity + 1 > el?.orderLimit
      )
        return toast.error(
          `You cannot purchase more tickets at this time. Please contact your event organiser`
        );
      if (quantity) {
        selectedTickets[selectedTicketIndex].quantity = quantity + 1;
        setSelectedTickets([...selectedTickets]);
      } else {
        selectedTickets[selectedTicketIndex].quantity = 1;
        setSelectedTickets([...selectedTickets]);
      }
    }
    if (
      action === "set" &&
      selectedTicketIndex > -1 &&
      quantity !== undefined &&
      quantity > -1
    ) {
      if (el.maxBuy && quantity > el.maxBuy)
        return toast.error(
          `You cannot purchase more than (${el.maxBuy}) of this ticket right now`
        );
      if (
        el?.orderLimit &&
        (el?.ticketsBought ?? 0) + quantity + 1 > el?.orderLimit
      )
        return toast.error(
          `You cannot purchase more tickets at this time. Please contact your event organiser`
        );
      selectedTickets[selectedTicketIndex].quantity = quantity;
      setSelectedTickets([...selectedTickets]);
    }
    if (action === "subtract" && selectedTicketIndex > -1) {
      if (totalValues.totalQuantity < 1) return;
      // if (totalValues.totalQuantity === 1)
      //   return toast.error("A minimum of ONE ticket must be bought");
      const quantity = selectedTickets[selectedTicketIndex].quantity ?? 0;
      if (quantity) {
        selectedTickets[selectedTicketIndex].quantity = quantity - 1;
        setSelectedTickets([...selectedTickets]);
      }
    }
  };

  const handleFetchDiscount = async () => {
    if (discount) {
      setDiscountCode("");
      return setDiscount(null);
    }
    if (!discountCode) return;
    try {
      setDiscountLoading(true);
      const q = query(
        collection(firestoredb, "discounts"),
        where("code", "==", discountCode)
      );
      const querySnapshot = await getDocs(q);
      const discount = querySnapshot.docs.map((doc) => ({
        ...(doc.data() as Discount),
        id: doc.id,
      }))[0];
      if (!discount || discount?.eventId !== event?.id) {
        return toast.error("Invalid discount code");
      }
      if (
        discount?.maxUses &&
        (discount?.useCount ?? 0) >= (discount?.maxUses ?? 0)
      ) {
        return toast.error("Sorry, this discount is not valid anymore");
      }
      if (discount?.disabled) {
        return toast.error("This discount code has been disabled");
      }
      setDiscount(discount);
    } catch (error) {
      console.error("error fetching discount", error);
      toast.error("Invalid discount code");
    } finally {
      setDiscountLoading(false);
    }
  };

  const hasAllUsersInput = useMemo(() => {
    if (!ticketOwners.length) return false;
    return ticketOwners.every(
      (ticketOwner) =>
        ticketOwner.firstName?.trim() &&
        ticketOwner.lastName?.trim() &&
        ticketOwner.email?.trim() &&
        ticketOwner.phoneNumber?.trim()
    );
  }, [ticketOwners]);

  const handleCreateUsers = async () => {
    try {
      setLoading(true);
      const usersData: UserBasicData[] = [];
      const batch = writeBatch(firestoredb);
      for (const el of ticketOwners) {
        const q = query(
          collection(firestoredb, "users"),
          where("email", "==", el.email.toLowerCase().trim()),
          where("archive", "==", false)
        );
        const querySnapshot = await getDocs(q);
        if (querySnapshot.docs.length) {
          const data = querySnapshot.docs[0].data() as UserBasicData;
          usersData.push({ email: data.email, id: querySnapshot.docs[0].id });
        } else {
          const newUserId = doc(collection(firestoredb, "users")).id;
          const docRef = doc(firestoredb, "users", newUserId);
          batch.set(docRef, {
            firstName: el.firstName,
            lastName: el.lastName,
            email: el.email.toLowerCase().trim(),
            createdAt: Timestamp.now(),
            archive: false,
            phoneNumber: el.phoneNumber,
            registeredFrom: event?.id ?? "",
            createdFrom: "Webapp",
          });
          usersData.push({ email: el.email, id: newUserId });
        }
      }
      await batch.commit();
      return usersData;
    } catch (error) {
      console.error("error creating users", error);
      setLoading(false);
      throw error;
    }
  };

  const updateDiscountUses = async (userId: string | undefined) => {
    if (discount != null && userId) {
      const docRef = doc(firestoredb, "discounts", discount.id);
      await updateDoc(docRef, {
        useCount: (discount.useCount ?? 0) + 1,
        usedBy: arrayUnion(userId),
        updatedAt: new Date(),
      });
    }
  };

  const handlePayment = async () => {
    const orderId = doc(collection(firestoredb, "tickets")).id;
    if (!primaryTicketOwner) return toast.error("Please select a primary user");
    const info = ticketOwners.map((tO) => {
      const ticket = selectedTickets.find((el) => el.name === tO.ticketType);
      return {
        firstName: tO.firstName,
        lastName: tO.lastName,
        email: tO.email,
        phoneNumber: tO.phoneNumber,
        ticketType: ticket?.id,
        ticketTitle: ticket?.name,
        ticketPrice: ticket?.price,
        otherInfo: Object.keys(tO?.info ?? {}).length
          ? Object.fromEntries(
            Object.entries(tO?.info ?? {}).filter(
              ([key]) => !requiredQuestionKeys.includes(key)
            )
          )
          : {},
        nonRequiredInfo: Object.keys(tO?.info ?? {}).length
          ? Object.fromEntries(
            Object.entries(tO?.info ?? {}).filter(
              ([key]) => !nonRequiredQuestionKeys.includes(key)
            )
          )
          : {},
        for: ticket?.for,
      };
    });
    try {
      setLoading(true);
      const metadata = {
        "update-3": true,
        firstName: primaryTicketOwner.firstName,
        lastName: primaryTicketOwner.lastName,
        email: primaryTicketOwner.email,
        numberOfTickets: info.length,
        // numberOfTickets: selectedTickets.reduce(
        //   (acc, el) => acc + (el?.quantity ?? 0),
        //   0
        // ),
        info, // for every ticket owner
        orderId: orderId,
        feeOnOrganiser: feeOnOrganiser(),
        amountPaid: totalValues.total,
        boughtBy: primaryTicketOwner.email,
        event: {
          id: event?.id,
          price: event?.amount,
          date: moment(formatFirestoreTimestamp(event?.date)).format(
            "Do MMM YYYY"
          ),
          location: event?.venue,
          time: moment(formatFirestoreTimestamp(event?.date)).format("hh:mm a"),
          organizer: event?.organiser,
          linkTitle: event?.linkTitle,
          linkUrl: event?.externalLink,
          discount: totalValues.discount,
          reach: event?.reach,
          title: event?.title,
        },
      } as const;
      if (discount != null) {
        const metadataDiscount: any = {
          discountId: discount.id,
          discountValue: discount.value,
          discountType: discount.type,
          discountCode: discount.code,
          priceAfterDiscount: totalValues.total,
        };
        if (discount?.ticketTypeId)
          metadataDiscount.discountTicketTypeId = discount.ticketTypeId;
        (metadata as any).discount = {
          ...metadataDiscount,
        };
      }
      openNombaPaymentPopup(primaryTicketOwner, metadata, orderId);
    } catch (error) {
      setLoading(false);
      if (error && typeof error === "object") {
        const err = error as { response?: { data?: { message?: string } } };
        toast.error(
          err.response?.data?.message ??
          "There was an issue making payment, please try again! "
        );
      } else {
        toast.error("There was an issue making payment, please try again!");
      }
    }
  };

  // const openPaystackPopup = (
  //   primaryTicketOwner: TicketOwner,
  //   metadata: any,
  //   orderId: string
  // ) => {
  //   openNombaPayment({
  //     amount: totalValues.total,
  //     email: primaryTicketOwner.email,
  //     metadata,
  //     onSuccess: () => {
  //       setPageStep(PAGE_STEPS.PAYMENT_SUCCESS);
  //     },
  //     onError: (error) => {
  //       toast.error(error);
  //       setLoading(false);
  //     },
  //     onCancel: () => {
  //       setLoading(false);
  //     },
  //   });
  //   // const popup = new Paystack();
  //   // popup.checkout({
  //   //   key: `${process.env.REACT_APP_PAYSTACK_PUBLIC_KEY}`,
  //   //   email: primaryTicketOwner.email,
  //   //   amount: totalValues.total * 100,
  //   //   metadata,
  //   //   reference: orderId,
  //   //   currency: "NGN",
  //   //   onSuccess: () => {
  //   //     setPageStep(PAGE_STEPS.PAYMENT_SUCCESS);
  //   //   },
  //   //   onCancel: () => {
  //   //     // stops loader
  //   //     setLoading(false);
  //   //   },
  //   //   onError: (error) => {
  //   //     console.log("error", error);
  //   //     toast.error("There was an issue making payment, please try again!");
  //   //     // stops loader
  //   //     setLoading(false);
  //   //   },
  //   // });
  // };

  const loadEmailTemplate = async (templateName: string, variables: any) => {
    const response = await fetch(`/templates/${templateName}.html`);
    let html = await response.text();

    for (const [key, value] of Object.entries(variables)) {
      const regex = new RegExp(`{{\\s*${key}\\s*}}`, "g");
      html = html.replace(regex, value as any);
    }

    return html;
  };

  const handleSendUserEmail = async (
    ticketOwner: TicketOwner,
    orderId: string,
    templateName = "ticket.3.email" // by default
  ) => {
    const emailVariables = {
      name: ticketOwner?.firstName ?? "",
      eventName: event?.title ?? "",
      eventDate:
        moment.unix(event?.date?.seconds).format("dddd, MMMM Do YYYY") ?? "",
      eventTime: moment.unix(event?.date?.seconds).format("HH:mm") ?? "",
      eventLocation: event?.venue ?? "",
      eventOrganizer: event?.organiser ?? "",
      contactName: event?.organiser ?? "",
      contactNumber: event?.phone ?? "",
      orderId: orderId,
      tickets: selectedTickets
        .filter((el) => el?.quantity && el.quantity > 0)
        .map(
          (
            el
          ) => `<tr style="padding:0; margin:0"> <td style="padding:0; margin:0">
                      <table width="100%" cellpadding="0" cellspacing="0" border="0">
                        <tr>
                          <td style="font-size: 14px">${el.quantity} x ${el.name
            }</td>
                          <td
                            align="right"
                            style="font-size: 14px; font-weight: 500"
                          >
                            ${el.price > 0
              ? `₦${formatMoney(el.price)}`
              : "FREE"
            }
                          </td>
                        </tr>
                      </table>
                    </td>
                  </tr>`
        ),
      discount: totalValues.discount,
      total: totalValues.total,
    };
    const html = await loadEmailTemplate(templateName, emailVariables);
    // const html = await loadEmailTemplate("email", emailVariables);

    await axios.post(
      process.env.REACT_APP_EMAIL_API!,
      {
        sender_email: "noreply@usedingpay.com",
        sender_name: "DingPay",
        email: ticketOwner?.email.toLowerCase(),
        message_type: "email",
        subject: `Your ${event?.title} ticket`,
        html: html,
      },
      {
        headers: {
          "X-Authorization": process.env.REACT_APP_EMAIL_API_KEY,
        },
      }
    );
  };

  const requiredQuestionKeys = otherQuestions
    .filter((el) => el.required)
    .map((el) => el.question);
  let nonRequiredQuestionKeys = otherQuestions
    .filter((el) => !el.required)
    .map((el) => el.question);

  if (referee && !nonRequiredQuestionKeys.includes("Referral Code")) {
    nonRequiredQuestionKeys.push("Referral Code");
  }

  const handleCreateTickets = async (usersData: UserBasicData[]) => {
    const orderId = doc(collection(firestoredb, "tickets")).id;
    const paystackRefId = doc(collection(firestoredb, "tickets")).id;
    try {
      const primaryUserData = usersData.find(
        (el) => el.email === primaryTicketOwner?.email
      );
      if (!usersData.length) return toast.error("No users found");
      const batch = writeBatch(firestoredb);
      for (let i = 0; i < usersData.length; i++) {
        const { id: userId } = usersData[i];
        const ticketOwner = ticketOwners[i];
        const ticketId = doc(collection(firestoredb, "tickets")).id;
        const docRef = doc(firestoredb, "tickets", ticketId);
        const ticket = selectedTickets.find(
          (el) => el.name === ticketOwner?.ticketType
        );
        let body = {
          createdAt: Timestamp.now(),
          userId,
          boughtAtOnce: totalValues.totalQuantity,
          boughtBy: primaryUserData?.id,
          eventId: event?.id,
          ticketType: ticket?.id,
          ticketTitle: ticket?.name,
          ticketPrice: ticket?.price,
          otherInfo: Object.keys(ticketOwner?.info ?? {}).length
            ? Object.fromEntries(
              Object.entries(ticketOwner?.info ?? {}).filter(
                ([key]) => !requiredQuestionKeys.includes(key)
              )
            )
            : {},
          nonRequiredInfo: Object.keys(ticketOwner?.info ?? {}).length
            ? Object.fromEntries(
              Object.entries(ticketOwner?.info ?? {}).filter(
                ([key]) => !nonRequiredQuestionKeys.includes(key)
              )
            )
            : {},
          uses: [],
          usesDates: [],
          price: event?.amount ?? 0,
          paystackReference: paystackRefId,
        } as const;
        if (discount != null) {
          (body as any).discount = {
            discountId: discount.id,
            discountValue: discount.value,
            discountType: discount.type,
            discountCode: discount.code,
            priceAfterDiscount: totalValues.total,
          };
        }
        batch.set(docRef, body);
      }
      await batch.commit();
      if (discount != null) await updateDiscountUses(primaryUserData?.id);
      setPageStep(PAGE_STEPS.PAYMENT_SUCCESS);
      // we send to the success page because bunce fails sometimes and bounces sometimes
      const emailPromises = [];
      // send email with order summary to primary user [main ticket owner]
      if (primaryTicketOwner)
        emailPromises.push(
          handleSendUserEmail(primaryTicketOwner, orderId, "email")
        ); // email is the template name
      // get the remaining ticketOwners
      const otherTicketOwners = ticketOwners.filter(
        (uD) => uD.email !== primaryUserData?.email
      );
      for (let i = 0; i < otherTicketOwners.length; i++) {
        const userData = otherTicketOwners[i];
        if (userData)
          emailPromises.push(
            handleSendUserEmail(userData, orderId, "ticket.3.email")
          );
      }
      await Promise.all(emailPromises);
      return toast.success("Tickets created successfully");
    } catch (error) {
      // toast.error(
      //   "Unfortunately, there was an error creating tickets. Please contact support"
      // );
      console.error("error creating tickets", error);
      setLoading(false);
      throw error;
    }
  };

  const handleBookTickets = async () => {
    if (!hasAllUsersInput) return toast.error("Please fill in all fields");
    setLoading(true);
    if (totalValues.total > 0) return handlePayment();
    const usersData: UserBasicData[] = await handleCreateUsers();
    await handleCreateTickets(usersData);
  };

  const handleAddTicketDataToDB = async (deviceType = "desktop") => {
    if (step === 1) return setStep(step + 1);
    if (step === 2) {
      // check to see if we have a primary user data complete
      if (
        !primaryTicketOwner?.email ||
        !primaryTicketOwner?.firstName ||
        !primaryTicketOwner?.lastName ||
        !primaryTicketOwner?.phoneNumber
      )
        return toast.error(
          "Please provide details for the selected primary user"
        );

      const ticketOwnerIndexWithInvalidEmail = ticketOwners
        .sort((a, b) => a.ticketIndexNumber - b.ticketIndexNumber)
        .findIndex((ticketOwner) => !isEmailValid(ticketOwner.email));

      if (ticketOwnerIndexWithInvalidEmail !== -1)
        return toast.error(
          `Please provide a valid email for ticket owner ${ticketOwnerIndexWithInvalidEmail + 1
          }`
        );

      const ticketOwnerIndexWithInvalidPhoneNumber = ticketOwners
        .sort((a, b) => a.ticketIndexNumber - b.ticketIndexNumber)
        .findIndex((ticketOwner) => !isPhoneValid(ticketOwner.phoneNumber));

      if (ticketOwnerIndexWithInvalidPhoneNumber !== -1)
        return toast.error(
          `Please provide a valid phone number for ticket owner ${ticketOwnerIndexWithInvalidPhoneNumber + 1
          }`
        );

      if (otherQuestions.length) {
        setStep(3);
      } else {
        if (deviceType === "desktop") return handleBookTickets();
        if (deviceType === "mobile") setStep(4);
      }
    }
    if (step === 3) {
      // the step with additional information
      if (deviceType === "desktop") return handleBookTickets();
      if (deviceType === "mobile") setStep(4);
    }
    if (step === 4) return handleBookTickets(); // only mobile screens have this screen
  };

  const disableContinueButton = () => {
    if (loading || !totalValues.totalQuantity) return true;

    const hasTicketOwnerWithAnUnfilledValue = ticketOwners.find(
      (ticketOwner) =>
        !ticketOwner?.email ||
        !ticketOwner?.firstName ||
        !ticketOwner?.lastName ||
        !ticketOwner?.phoneNumber
    );

    const primaryUserHasAnEmptyField =
      !primaryTicketOwner?.email ||
      !primaryTicketOwner?.firstName ||
      !primaryTicketOwner?.lastName ||
      !primaryTicketOwner?.phoneNumber;

    if (
      step === 2 &&
      (hasTicketOwnerWithAnUnfilledValue ||
        // hasTicketOwnerWithInvalidEmail ||
        // hasTicketOwnerWithInvalidPhoneNumber ||
        primaryUserHasAnEmptyField)
    )
      return true;

    if (step === 3 && otherQuestions.length) {
      const requiredQuestions = otherQuestions.filter((el) => el.required);
      if (requiredQuestions.length) {
        // Check if any ticket owner is missing answers for required questions
        const hasUnansweredRequiredQuestions = ticketOwners.some(
          (ticketOwner) => {
            return requiredQuestions.some((question) => {
              const value = ticketOwner.info?.[question.question];
              // Check if the field is missing or empty
              return value === undefined || value === null || value === "";
            });
          }
        );
        return hasUnansweredRequiredQuestions;
      }
      return false;
    }
    return false;
  };

  return (
    <div className='buy-ticket-page'>
      <CustomerHeader
        children={
          <div className='header-buttons'>
            <button
              className='btn-flex'
              onClick={() => {
                if (step === 4 && !otherQuestions.length) return setStep(2);
                if (step > 1) return setStep(step - 1);
                handleMoveToPreviousScreen();
              }}
            >
              <i className='bi bi-chevron-left'></i>
              <p>Back</p>
            </button>
            <button
              className='btn-flex'
              onClick={handleMoveToPreviousScreen} // goes to event details
            >
              <i className='bi bi-x'></i>
              <p>Cancel</p>
            </button>
          </div>
        }
      />
      <div className='buy-ticket-container'>
        <div className='buy-ticket-container-left'>
          <a
            className='back-btn-link'
            href='#ß'
            onClick={() => {
              if (step > 1) return setStep(step - 1);
              handleMoveToPreviousScreen();
            }}
          >
            <button className='back-btn'>
              <i className='bi bi-arrow-left'></i>
            </button>
          </a>
          <div className='buy-ticket-container-left_step-container'>
            <div
              className={`buy-ticket-container-left_step-container-step ${step === 1 ? "active" : ""
                }`}
              onClick={() => {
                if (loading) return;
                return setStep(1);
              }}
            >
              <p>1</p>
              <p>Tickets</p>
            </div>
            <div
              className={`buy-ticket-container-left_step-container-step ${step === 2 ? "active" : ""
                }`}
              onClick={() => {
                if (loading || !totalValues.totalQuantity) return;
                return setStep(2);
              }}
            >
              <p>2</p>
              <p>Contact</p>
            </div>
            {otherQuestions.length ? (
              <div
                className={`buy-ticket-container-left_step-container-step ${step === 3 ? "active" : ""
                  }`}
              >
                <p>3</p>
                <p>Other Information</p>
              </div>
            ) : null}
            <div className='buy-ticket-container-left_step-container-step'>
              <p>{otherQuestions?.length ? 4 : 3}</p>
              <p>All set!</p>
            </div>
          </div>
          {step === 1 ? (
            <BuyTicketStepOne
              ticketTypes={selectedTickets.map((el) => ({
                ...el,
                price: Number(el.price),
              }))}
              handleEditTicket={handleEditTicket}
            />
          ) : step === 2 ? (
            <BuyTicketStepTwo
              totalValues={totalValues}
              selectedTickets={selectedTickets}
              setTicketOwners={(val) => {
                setTicketOwners(val);
              }}
              ticketOwners={ticketOwners}
              setPrimaryTicketOwner={setPrimaryTicketOwner}
              primaryTicketOwner={primaryTicketOwner}
            />
          ) : step === 3 ? (
            <BuyTicketStepThree
              questions={otherQuestions}
              setOtherInfoAnswers={(val) => setOtherInfoAnswers(val)}
              otherInfoAnswers={otherInfoAnswers}
              ticketOwners={ticketOwners}
              setTicketOwners={setTicketOwners}
            />
          ) : step === 4 ? (
            <OrderSummaryMobileView
              step={step}
              selectedTickets={selectedTickets}
              totalValues={totalValues}
              handleBookTickets={handleBookTickets}
              ticketOwners={ticketOwners}
              loading={loading}
              primaryTicketOwner={primaryTicketOwner}
              handleAddTicketDataToDB={() => handleAddTicketDataToDB()}
              handleFetchDiscount={handleFetchDiscount}
              discountCode={discountCode}
              setDiscountCode={setDiscountCode}
              discountLoading={discountLoading}
              discount={discount}
              setDiscount={setDiscount}
              disableContinueButton={disableContinueButton()}
            />
          ) : null}
        </div>
        {/* // for desktop screens */}
        <OrderSummary
          step={step}
          selectedTickets={selectedTickets}
          totalValues={totalValues}
          handleBookTickets={handleBookTickets}
          ticketOwners={ticketOwners}
          loading={loading}
          primaryTicketOwner={primaryTicketOwner}
          handleAddTicketDataToDB={() => handleAddTicketDataToDB()}
          handleFetchDiscount={handleFetchDiscount}
          discountCode={discountCode}
          setDiscountCode={setDiscountCode}
          discountLoading={discountLoading}
          discount={discount}
          setDiscount={setDiscount}
          disableContinueButton={disableContinueButton()}
        />
      </div>
      {/* // right container  */}
      {/* ----- Mobile continue button ------! */}
      <div className='continue-btn-container mobile-continue-btn-container'>
        <button
          className='continue-btn'
          onClick={() => handleAddTicketDataToDB("mobile")}
          disabled={disableContinueButton()}
        >
          {loading ? <Loading /> : "Continue"}
        </button>
      </div>
      {/* ----- Mobile continue button ------! */}
      {paymentModal && (
        <NombaPaymentModal
          checkoutLink={paymentModal.checkoutLink}
          onClose={handlePaymentModalClose}
        />
      )}
    </div>
  );
}
